import { Ban, KeyRound, Lock, ShieldCheck } from "lucide-react";
import { makeTr, type LandingLocale } from "@/lib/landing/locale";
import { Container, Section, SectionHeading } from "./primitives";

/**
 * How the token and the data are protected. Every line here is backed by code:
 * Fio read endpoints only (fetch-fio-transactions, download-fio-statement),
 * AES-GCM token encryption (_shared/fio-token.ts) and organization RLS
 * (migrations/*_org_rls.sql). Client fields are not claimed: their plaintext
 * columns are still written (see useClients.ts).
 */
export function TrustSection({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);
  const points = [
    {
      icon: <KeyRound className="size-5" />,
      title: tr("Žádné heslo do banky", "No banking password"),
      text: tr(
        "Token vytvoříte sami a ve Fio ho kdykoli zrušíte.",
        "You create the token and can revoke it in Fio."
      ),
    },
    {
      icon: <Ban className="size-5" />,
      title: tr("Neumí posílat platby", "Cannot send payments"),
      text: tr("Z Fio API jen čte pohyby a výpisy.", "It only reads transactions and statements."),
    },
    {
      icon: <Lock className="size-5" />,
      title: tr("Šifrovaný token", "An encrypted token"),
      text: tr("Ukládáme ho šifrovaný (AES-GCM).", "We store it encrypted (AES-GCM)."),
    },
    {
      icon: <ShieldCheck className="size-5" />,
      title: tr("Data oddělená po firmách", "Data kept per company"),
      text: tr(
        "Row Level Security pustí k datům jen členy firmy.",
        "Row Level Security admits only the company's members."
      ),
    },
  ];

  return (
    <Section id="bezpecnost" labelledBy="trust-title" tone="tinted">
      <Container>
        <SectionHeading
          id="trust-title"
          eyebrow={tr("Bezpečnost", "Security")}
          title={tr("Bezpečnost a ochrana dat", "Security and data protection")}
          lead={tr(
            "Zettl vaše peníze jen vidí, nikdy s nimi nehýbe.",
            "Zettl only sees your money, it never moves it."
          )}
        />
        <ul className="mt-12 grid gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
          {points.map((p) => (
            <li key={p.title}>
              <span className="flex size-11 items-center justify-center rounded-xl border border-primary/25 bg-primary/10 text-[hsl(var(--action))] dark:text-primary">
                {p.icon}
              </span>
              <h3 className="mt-4 text-base font-semibold tracking-tight">{p.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{p.text}</p>
            </li>
          ))}
        </ul>
      </Container>
    </Section>
  );
}
